import React from 'react';
import { RefreshCw, Eye, BarChart3 } from 'lucide-react';
import * as Icons from 'lucide-react';
import {
  BarChart as RechartsBarChart,
  LineChart as RechartsLineChart,
  PieChart as RechartsPieChart,
  Bar,
  Line,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const PreviewPanel = ({ componentType, entityName, config, data, loading, onRefresh }) => {
  if (!entityName) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center h-full flex items-center justify-center">
        <div>
          <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Select an entity to see a preview
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center h-full flex items-center justify-center">
        <div>
          <RefreshCw className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          <Eye className="w-5 h-5 inline mr-2" />
          Live Preview
        </h2>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="btn btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        {componentType === 'StatCard' && (
          <StatCardPreview config={config} data={data} />
        )}

        {componentType === 'Table' && (
          <TablePreview config={config} data={data} />
        )}

        {componentType === 'BarChart' && (
          <ChartPreview config={config} data={data} chartType="bar" />
        )}

        {componentType === 'LineChart' && (
          <ChartPreview config={config} data={data} chartType="line" />
        )}

        {componentType === 'PieChart' && (
          <ChartPreview config={config} data={data} chartType="pie" />
        )}
      </div>
    </div>
  );
};

const StatCardPreview = ({ config, data }) => {
  const IconComponent = Icons[config.icon] || Icons.Users;
  const colorClasses = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', lightBg: 'bg-blue-50 dark:bg-blue-900/20' },
    green: { bg: 'bg-green-500', text: 'text-green-600', lightBg: 'bg-green-50 dark:bg-green-900/20' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-600', lightBg: 'bg-purple-50 dark:bg-purple-900/20' },
    red: { bg: 'bg-red-500', text: 'text-red-600', lightBg: 'bg-red-50 dark:bg-red-900/20' },
    yellow: { bg: 'bg-yellow-500', text: 'text-yellow-600', lightBg: 'bg-yellow-50 dark:bg-yellow-900/20' },
    indigo: { bg: 'bg-indigo-500', text: 'text-indigo-600', lightBg: 'bg-indigo-50 dark:bg-indigo-900/20' }
  };

  const colors = colorClasses[config.color] || colorClasses.blue;

  const formatValue = (value) => {
    if (!value && value !== 0) return '0';

    switch (config.format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'decimal':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US').format(value);
    }
  };

  return (
    <div className={`${colors.lightBg} rounded-lg p-6 border border-gray-200 dark:border-gray-700`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {config.label || 'Stat Card'}
          </p>
          <p className={`text-3xl font-bold ${colors.text}`}>
            {data ? formatValue(data.value) : '...'}
          </p>
        </div>
        <div className={`p-3 ${colors.bg} rounded-lg text-white`}>
          <IconComponent className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};

// Format number based on field format configuration
const formatNumber = (value, format, currency = 'USD') => {
  if (value == null || value === '') return '-';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return value;
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(numValue);
    case 'decimal':
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    case 'integer':
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format(numValue);
    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue / 100);
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(numValue);
  }
};

// Format date based on field format configuration
const formatDate = (value, format) => {
  if (value == null || value === '') return '-';
  
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else {
    return value;
  }
  
  if (isNaN(date.getTime())) return value;
  
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  switch (format) {
    case 'relative':
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) !== 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? 's' : ''} ago`;
    case 'datetime':
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    case 'iso':
      return date.toISOString().split('T')[0];
    case 'iso-datetime':
      return date.toISOString().replace('T', ' ').split('.')[0];
    case 'long':
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    case 'short':
      return new Intl.DateTimeFormat('en-US', {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric'
      }).format(date);
    case 'date':
    default:
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
  }
};

// Check if field type is numeric
const isNumericField = (type) => {
  const numericTypes = ['integer', 'decimal', 'number', 'int', 'float', 'double', 'bigint', 'currency'];
  return numericTypes.includes(type?.toLowerCase());
};

// Check if field type is date
const isDateField = (type) => {
  const dateTypes = ['date', 'datetime', 'timestamp'];
  return dateTypes.includes(type?.toLowerCase());
};

// Format cell value based on field configuration
const formatCellValue = (value, field) => {
  if (value == null || value === '') return '-';
  
  if (isNumericField(field.type) && field.format) {
    return formatNumber(value, field.format, field.currency);
  }
  
  if (isDateField(field.type) && field.format) {
    return formatDate(value, field.format);
  }
  
  return String(value);
};

const TablePreview = ({ config, data }) => {
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        No data available. Click Refresh to load data.
      </div>
    );
  }

  const fields = config.fields || [];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            {fields.map(field => (
              <th
                key={field.name}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                {field.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              {fields.map(field => (
                <td
                  key={field.name}
                  className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
                >
                  {formatCellValue(row[field.name], field)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {data.pagination && (
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Showing {data.rows.length} of {data.pagination.total} records
        </div>
      )}
    </div>
  );
};

const ChartPreview = ({ config, data, chartType }) => {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <p>No data available. Click Refresh to load data.</p>
        {(!config.xAxis && chartType !== 'pie') && (
          <p className="text-sm mt-2 text-gray-400">Please configure X-axis field</p>
        )}
        {(!config.series || config.series.length === 0) && (
          <p className="text-sm mt-2 text-gray-400">Please add at least one data series</p>
        )}
      </div>
    );
  }

  const chartData = data.data || [];
  const series = config.series || [];
  
  // Color palettes for pie charts (matching ChartEditor)
  const COLOR_PALETTES = {
    vibrant: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'],
    pastel: ['#A5D8FF', '#A7F3D0', '#FDE68A', '#FCA5A5', '#C4B5FD', '#67E8F9', '#F9A8D4', '#D9F99D', '#FDBA74', '#C7D2FE'],
    monochrome: ['#1F2937', '#374151', '#4B5563', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB', '#F3F4F6', '#F9FAFB', '#FFFFFF'],
    warm: ['#F97316', '#EF4444', '#DC2626', '#F59E0B', '#FBBF24', '#FCD34D', '#FED7AA', '#FFEDD5', '#FFF7ED', '#FEF2F2'],
    cool: ['#3B82F6', '#2563EB', '#1D4ED8', '#10B981', '#059669', '#06B6D4', '#0891B2', '#0EA5E9', '#60A5FA', '#93C5FD'],
    sunset: ['#F97316', '#EC4899', '#8B5CF6', '#6366F1', '#3B82F6', '#10B981', '#84CC16', '#FBBF24', '#F59E0B', '#EF4444']
  };
  
  // Default colors for bar/line charts (fallback)
  const DEFAULT_COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'
  ];

  if (chartType === 'pie') {
    // Pie chart: data should have 'category' and 'value' fields
    const pieData = chartData.map(item => ({
      name: item.category || item.name || 'Unknown',
      value: parseFloat(item.value || 0)
    }));

    const showDataLabels = config.showDataLabels !== false; // Default to true
    const showAxisLabels = config.showAxisLabels !== false; // Default to true (for legend)
    
    // Use selected palette or default to vibrant
    const selectedPalette = COLOR_PALETTES[config.colorPalette || 'vibrant'] || COLOR_PALETTES.vibrant;

    return (
      <div className="w-full">
        <ResponsiveContainer width="100%" height={400}>
          <RechartsPieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={showDataLabels}
              label={showDataLabels ? ({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)` : false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={selectedPalette[index % selectedPalette.length]} />
              ))}
            </Pie>
            <Tooltip />
            {showAxisLabels && <Legend />}
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar/Line chart: data should have 'category' and series fields
  const categoryKey = 'category';
  
  // Check if we have series configured
  if (!series || series.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <p>No series configured. Please add at least one data series.</p>
      </div>
    );
  }
  
  // Map series to keys - use the label or field name as the key
  // The backend returns data with keys matching the series labels/fields (sanitized)
  const firstRow = chartData[0] || {};
  const availableKeys = Object.keys(firstRow);
  
  const seriesKeys = series.map((s, index) => {
    // Generate the expected key (same logic as backend)
    let expectedKey = s.label || s.field || `series${index + 1}`;
    expectedKey = expectedKey.replace(/[^a-zA-Z0-9_]/g, '_');
    if (/^[0-9]/.test(expectedKey)) {
      expectedKey = `series_${expectedKey}`;
    }
    
    // Try to find the key in the data
    let key = null;
    if (expectedKey in firstRow) {
      key = expectedKey;
    } else {
      // Try case-insensitive match
      const foundKey = availableKeys.find(k => k.toLowerCase() === expectedKey.toLowerCase());
      if (foundKey) {
        key = foundKey;
      } else {
        // Try partial match
        const partialMatch = availableKeys.find(k => 
          k.toLowerCase().includes((s.field || '').toLowerCase()) ||
          (s.label && k.toLowerCase().includes(s.label.toLowerCase()))
        );
        key = partialMatch || expectedKey;
      }
    }
    
    return { 
      key, 
      label: s.label || s.field || `Series ${index + 1}`, 
      color: s.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length] 
    };
  }).filter(s => s.key && chartData[0] && s.key in chartData[0]); // Only include series that exist in data

  if (seriesKeys.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <p>No series configured. Please add at least one data series.</p>
      </div>
    );
  }

  const ChartComponent = chartType === 'bar' ? RechartsBarChart : RechartsLineChart;
  const DataComponent = chartType === 'bar' ? Bar : Line;
  const showDataLabels = config.showDataLabels !== false; // Default to true
  const showAxisLabels = config.showAxisLabels !== false; // Default to true
  
  // Get bar orientation for bar charts
  const barLayout = chartType === 'bar' ? (config.barOrientation || 'vertical') : null;
  const isHorizontalBar = barLayout === 'horizontal';

  // Data label formatter
  const renderLabel = showDataLabels ? (entry) => {
    const value = entry[seriesKeys[0]?.key] || entry.value || 0;
    return value;
  } : undefined;

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent 
          data={chartData} 
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          layout={isHorizontalBar ? 'horizontal' : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {showAxisLabels ? (
            <>
              {isHorizontalBar ? (
                <>
                  <XAxis type="number" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                  <YAxis 
                    dataKey={categoryKey} 
                    type="category" 
                    width={80}
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                </>
              ) : (
                <>
                  <XAxis 
                    dataKey={categoryKey} 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {isHorizontalBar ? (
                <>
                  <XAxis type="number" hide={true} />
                  <YAxis dataKey={categoryKey} type="category" hide={true} />
                </>
              ) : (
                <>
                  <XAxis 
                    dataKey={categoryKey} 
                    hide={true}
                  />
                  <YAxis 
                    hide={true}
                  />
                </>
              )}
            </>
          )}
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#fff', 
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <Legend />
          {seriesKeys.map((series, index) => (
            <DataComponent
              key={series.key}
              type="monotone"
              dataKey={series.key}
              name={series.label || `Series ${index + 1}`}
              stroke={series.color}
              fill={series.color}
              label={showDataLabels ? renderLabel : false}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default PreviewPanel;

